// Turns a failed workspace-status fetch into what kore's ErrorPage needs: a
// status bucket for its badge, plus the title/detail to show underneath.
//
// The server's RFC 9457 title is the primary source of truth — for most
// errors (403 "not allowed", 404 "workspace not found") it is the only
// human-readable field the server sends, `detail` is frequently absent. The
// hand-written copy below is only a fallback for the two cases where there
// is no server title to show: the failure never reached the API at all (a
// connectivity problem — not an ApiError), or it came back as an opaque,
// non-JSON body (an intermediary's error page, not ewt-server's).

import type { ErrorPageStatus } from "@pej/kore/components";
import { ApiError } from "@/api/error";

export interface StatusErrorDescription {
    status: ErrorPageStatus;
    pd: {
        title: string;
        detail?: string;
        requestId?: string;
    };
}

type Bucket = "403" | "404" | "500" | "OFFLINE";

const FALLBACK: Record<Bucket, { title: string; detail: string }> = {
    "403": {
        title: "You don't have access to this workspace",
        detail: "Ask whoever manages it to add you, then try again.",
    },
    "404": {
        title: "This workspace doesn't exist",
        detail: "Check the link, or ask whoever shared it with you.",
    },
    "500": {
        title: "Something went wrong",
        detail: "Try again in a moment.",
    },
    OFFLINE: {
        title: "Can't connect right now",
        detail: "Check your connection, then try again.",
    },
};

function bucketOf(httpStatus: number): "403" | "404" | "500" {
    if (httpStatus === 403) return "403";
    if (httpStatus === 404) return "404";
    return "500";
}

export function describeStatusError(error: unknown): StatusErrorDescription {
    if (error instanceof ApiError) {
        const status = bucketOf(error.status);
        if (error.title) {
            return { status, pd: { title: error.title, detail: error.detail, requestId: error.requestId } };
        }
        return { status, pd: FALLBACK[status] };
    }
    // Not an ApiError: fetch itself rejected (offline, DNS, a cross-origin
    // redirect with no CORS headers) before any response came back.
    return { status: "OFFLINE", pd: FALLBACK.OFFLINE };
}
